import {
  AuthActions,
  AuthRedirectService,
  AuthRedirectStorageService,
  AuthService,
  AuthStorageService,
  OAuthLibWrapperService,
  OCC_USER_ID_CURRENT,
  RoutingService,
  StateWithClientAuth,
  UserIdService,
} from '@spartacus/core';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {Store} from '@ngrx/store';
import {isPlatformBrowser} from '@angular/common';
import {OAuthService} from 'angular-oauth2-oidc';
import {Router} from '@angular/router';
import {first} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {DcpGlobalMessageService, IAuthService} from '@dcp/configurator';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpAuthService extends AuthService implements IAuthService {
  constructor(
    store: Store<StateWithClientAuth>,
    protected router: Router,
    protected routingService: RoutingService,
    @Inject(PLATFORM_ID) public platformId: string,
    protected userIdService: UserIdService,
    protected oAuthLibWrapperService: OAuthLibWrapperService,
    protected oAuthService: OAuthService,
    protected authStorageService: AuthStorageService,
    protected authRedirectService: AuthRedirectService,
    protected authRedirectStorageService: AuthRedirectStorageService,
    protected dcpGlobalMessageService: DcpGlobalMessageService,
    protected userFacade: UserAccountFacade
  ) {
    super(store, userIdService, oAuthLibWrapperService, authStorageService, authRedirectService, routingService);

    if (isPlatformBrowser(this.platformId)) {
      this.oAuthService.redirectUri = `${window.location.origin}/auth`;
    }
  }

  async checkOAuthParamsInUrl(): Promise<void> {
    if (isPlatformBrowser(this.platformId) && !this.isBaselineFlow()) {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        this.oAuthService.setupAutomaticSilentRefresh();
        const result = await this.oAuthService.tryLogin();
        this.addRemovedQueryParam(searchParams);
        const token = this.authStorageService.getItem('access_token');

        // We get the result in the code flow even if we did not log in that's why we also need to check if we have access_token
        if (result && token) {
          this.userIdService.setUserId(OCC_USER_ID_CURRENT);

          this.userFacade
            .get()
            .pipe(first(), untilDestroyed(this))
            .subscribe(() => {
              this.store.dispatch(new AuthActions.Login());
            });
        }
      } catch (e) {
        this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
      }
    }
  }

  private isBaselineFlow() {
    return window?.location?.href?.includes('/baseline/');
  }

  /**
   * oAuthLibWrapperService.tryLogin removes all parameters ending with 'code'
   * this method brings back 'campaigncode' query param
   */
  addRemovedQueryParam(searchParams: URLSearchParams) {
    if (isPlatformBrowser(this.platformId)) {
      const campaignCode = searchParams.get('campaigncode');
      if (campaignCode) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('campaign');
        searchParams.set('campaigncode', campaignCode);
        window.history.replaceState(
          null,
          window.name,
          location.origin + location.pathname + '?' + searchParams.toString() + location.hash
        );
      }
    }
  }

  /**
   * Configure the Authentication Request and saves a redirect url.
   * Spartacus handles the redirect logic.
   * After that the user is forwarded Identity Provider.
   *
   * @param redirectUrl optional parameter to the relative route to redirect to after login,
   * when empty the request will be redirected to the current page
   */
  login(redirectUrl?: string): boolean {
    if (isPlatformBrowser(this.platformId)) {
      this.authRedirectStorageService.setRedirectUrl(redirectUrl ?? this.router.url);
      return super.loginWithRedirect();
    }
  }

  coreLogout(): Promise<void> {
    return new Promise((resolve) => {
      this.userIdService.clearUserId();
      this.oAuthService.logOut();
      this.store.dispatch(new AuthActions.Logout());
      resolve();
    });
  }

  /**
   * When an authentication error occurs, stop the silent refresh and do further cleanup by logging out the user
   */
  onAuthenticationError(): void {
    this.coreLogout()
      .then(() => {
      })
      .catch((error) => {
        console.error('Error during DcpAuthService onAuthenticationError logout:', error);
      });
  }
}
